import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'lote-defaults';

export interface LoteDefaults {
  proveedor: string;
  numeroFactura: string;
  activo: boolean;
}

const defaultValues: LoteDefaults = {
  proveedor: '',
  numeroFactura: '',
  activo: false,
};

function readFromStorage(): LoteDefaults {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return { ...defaultValues, ...parsed };
    }
  } catch {
    // ignore
  }
  return { ...defaultValues };
}

function writeToStorage(values: LoteDefaults) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
}

export function useLoteDefaults() {
  const [defaults, setDefaultsState] = useState<LoteDefaults>(readFromStorage);

  useEffect(() => {
    writeToStorage(defaults);
  }, [defaults]);

  const setDefaults = useCallback((values: Partial<LoteDefaults>) => {
    setDefaultsState(prev => {
      const next = { ...prev, ...values };
      writeToStorage(next);
      return next;
    });
  }, []);

  const toggleActivo = useCallback(() => {
    setDefaultsState(prev => {
      const next = { ...prev, activo: !prev.activo };
      writeToStorage(next);
      return next;
    });
  }, []);

  const clearDefaults = useCallback(() => {
    setDefaultsState({ ...defaultValues });
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    defaults,
    setDefaults,
    toggleActivo,
    clearDefaults,
  };
}
